import { useState } from 'react'
import { Button, Select, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { Can } from '@/shared/auth/Can'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DataTable } from '@/shared/ui/table/DataTable'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { VenueFormModal } from '@/features/venue/VenueFormModal'
import { VenueStateAction } from '@/features/venue/VenueStateAction'
import {
  VENUE_DEFAULT_STATE,
  VENUE_STATES,
  VENUE_STATE_ALL,
  VENUE_STATE_FILTER,
  fetchVenuePage,
  isVenueState,
  type Venue,
} from '@/features/venue/venueApi'

const STATE_COLOR: Record<Venue['state'], string> = {
  ACTIVE: 'green',
  SUSPENDED: 'gold',
  CLOSED: 'default',
}

/**
 * Ob'ektlar (venue) ro'yxati.
 *
 * ⚠️ SAHIFALASH, QIDIRUV VA SARALASH — SERVERDA (2026-08-20).
 * `GET /api/venues` endi `page`/`size`/`search`/`sortField`/`sortOrder`
 * ni qabul qiladi va `{rows,total,page,size}` qaytaradi. Ilgari bu yerda
 * to'liq ro'yxat olinar va qidiruv mijozda bo'lardi — endi u faqat joriy
 * sahifani ko'rardi, ya'ni «topilmadi» degan javob YOLG'ON bo'lardi.
 *
 * ⚠️ SUKUT TARTIB O'ZGARDI: ilgari server `name` bo'yicha (alifbo)
 * qaytarardi, endi `CREATED_AT DESC` — oxirgi ochilgan ob'ekt tepada.
 * Alifbo kerak bo'lsa «Nomi» ustuni saralanadi.
 *
 * ⚠️ HOLAT FILTRI SERVERDA. Ob'ekt hech qachon o'chirilmaydi (mehmon
 * jurnali qonun bo'yicha saqlanadi — `HOLAT.md` §5), ya'ni yopilganlar
 * to'planib boradi.
 *
 * <h2>⚠️ STANDART TANLOV «BARCHASI» EMAS, «FAOL» (egasi qarori 2026-08-20)</h2>
 * Yopilgan va to'xtatilgan ob'ektlar sukut bo'yicha KO'RINMAYDI. Ular
 * yo'qolmaydi: holat filtrida o'z tanlovi bor va so'rov serverga
 * ROSTDAN boradi — ya'ni to'xtatilganni QAYTA YOQISH yo'li joyida.
 *
 * ⚠️ Filtr JADVAL USTUNIDA ham e'lon qilingan va bu MAJBURIY:
 * `DataTable.handleChange` filtrlarni antd bergan ro'yxatdan qayta
 * quradi. Ustunsiz filtr sahifa almashtirilishi bilan jimgina sukutga
 * qaytardi — operator 2-sahifada yopilganlarni yo'qotib qo'yardi va
 * hech qanday xato ko'rmasdi.
 */
export function VenuesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [formOpen, setFormOpen] = useState(false)

  /*
    ⚠️ Holat tanlagichi jadval holatining O'ZINI yozadi — `DataTable`
    o'sha manbadan o'qiydi. Alohida `useState` ikkinchi haqiqat manbai
    bo'lardi va tanlov bilan so'rov ajralib ketardi.
  */
  const { state: tableState, patch: patchTable } = useTableUrlState()
  const rawState = tableState.filters[VENUE_STATE_FILTER]?.[0]
  /**
   * Ekrandagi tanlov. ⚠️ Noma'lum qiymat SUKUTGA qaytadi — `fetchVenuePage`
   * ham aynan shunday qiladi (`venueStateParam`), ya'ni ekran va so'rov
   * bir xil narsani ko'rsatadi.
   */
  const stateFilter: string =
    rawState === VENUE_STATE_ALL || (rawState !== undefined && isVenueState(rawState))
      ? rawState
      : VENUE_DEFAULT_STATE

  const stateOptions = [
    ...VENUE_STATES.map((s) => ({ value: s, label: t(`venueState.${s}`) })),
    { value: VENUE_STATE_ALL, label: t('venue.stateFilterAll') },
  ]

  const columns: ColumnsType<Venue> = [
    {
      key: 'name',
      title: t('venue.name'),
      sorter: true,
      render: (_, row) => (
        <Typography.Link onClick={() => navigate(`/venues/${row.id}`)}>
          <Typography.Text strong>{row.name}</Typography.Text>
        </Typography.Link>
      ),
    },
    { key: 'code', title: t('venue.code'), width: 140, sorter: true, render: (_, row) => row.code },
    {
      key: 'kind',
      title: t('venue.kind'),
      width: 140,
      sorter: true,
      render: (_, row) => t(`venueKind.${row.kind}`),
    },
    {
      key: 'contact',
      title: t('venue.contactPhone'),
      width: 180,
      render: (_, row) => row.contactPhone ?? '—',
    },
    { key: 'funding', title: t('venue.funding'), width: 130, render: (_, row) => t(`venueFunding.${row.funding}`) },
    {
      /*
        ⚠️ Ustun kaliti FILTR kaliti bilan bir xil va `filters` MAJBURIY —
        sabab sinf javadoc'ida. Ro'yxat sarlavhadagi tanlagich bilan
        AYNAN bir xil bo'lishi shart, aks holda antd bilmaydigan qiymat
        sahifa almashtirilishida tushib qolardi.
      */
      key: VENUE_STATE_FILTER,
      title: t('subscribers.columns.status'),
      width: 120,
      sorter: true,
      filters: stateOptions.map((o) => ({ text: o.label, value: o.value })),
      filterMultiple: false,
      render: (_, row) => <Tag color={STATE_COLOR[row.state]}>{t(`venueState.${row.state}`)}</Tag>,
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      /*
        ⚠️ `fixed: 'right'` — ustun jadval bilan SURILMAYDI.

        Jadvalda gorizontal scroll bor va amallar ustuni o'ng chekkada
        qolib ekrandan chiqib ketardi: 1000px oynada tugmalar 1389px
        da, ko'rish maydoni esa 951px da tugardi. Ya'ni operator
        "To'xtatish" va "Yopish" ni umuman ko'rmasdi.

        ⚠️ Bu nuqson uzoq vaqt SEZILMAGAN, chunki o'lchov asboblari
        uni ko'rmasdi — katak siqilmagan, matn bo'linmagan, ustun
        shunchaki ko'rish maydonidan tashqarida edi. Tekshiruv keyin
        qo'shildi (`tests/page-sweep.mjs`).
      */
      fixed: 'right',
      // ⚠️ Huquq va holat shartlari `VenueStateAction` ning O'ZIDA qoladi —
      // qatorda faqat chizilishi o'zgardi (uchta tugma → bitta menyu).
      render: (_, row) => <VenueStateAction venue={row} size="small" variant="menu" />,
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.venues')}
        description={t('venue.intro')}
        extra={
          <Can permission="venue.create">
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setFormOpen(true)}>
              {t('venue.createTitle')}
            </Button>
          </Can>
        }
      />

      {/*
        ⚠️ Ro'yxat XATOSI uchun alohida banner YO'Q — uni `DataTable`
        o'zi chizadi va "qayta urinish" tugmasini ham beradi.
      */}
      <DataTable<Venue>
        queryKey={queryKeys.venues.all}
        fetchPage={fetchVenuePage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="venues"
        stickyFirstColumn
        searchable
        searchPlaceholder={t('venue.searchPlaceholder')}
        scrollX={1100}
        emptyTitle={t('venue.emptyTitle')}
        emptyDescription={t('venue.emptyHint')}
        /*
          ⚠️ Bo'sh ro'yxat endi «ob'ekt yo'q» degani EMAS: sukut
          ko'rinishda yopilgan va to'xtatilganlar yashirin. Ikkinchi yo'l
          ko'rsatilmasa operator ular butunlay o'chib ketgan deb o'ylardi.
        */
        emptyAction={{
          label: t('venue.stateFilterAll'),
          onClick: () =>
            patchTable({
              filters: { ...tableState.filters, [VENUE_STATE_FILTER]: [VENUE_STATE_ALL] },
            }),
        }}
        toolbarExtra={
          /*
            ⚠️ `allowClear` YO'Q va tanlov DOIM ko'rinib turadi: bo'sh
            maydon «filtr qo'yilmagan» deb o'qilardi, ro'yxat esa aslida
            faqat faollarni ko'rsatib turgan bo'lardi — yopilgan ob'ekt
            qayerga ketganini operator hech qayerdan bilmasdi.
          */
          <Tooltip title={t('dataTable.closedHiddenHint')}>
            <Select<string>
              value={stateFilter}
              onChange={(value) =>
                patchTable({
                  filters: { ...tableState.filters, [VENUE_STATE_FILTER]: [value] },
                })
              }
              style={{ minWidth: 180 }}
              aria-label={t('venue.stateFilter')}
              options={stateOptions}
            />
          </Tooltip>
        }
      />

      <VenueFormModal open={formOpen} venue={null} onClose={() => setFormOpen(false)} />
    </>
  )
}
